import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  linkedSignal,
  model,
  output,
  resource,
  signal,
  untracked,
  viewChild,
  viewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClarityModule, ClrForm, ClrLoadingState } from '@clr/angular';
import { ToastrService } from 'ngx-toastr';
import { DeselectSingleSelectedDirective } from 'src/app/directives/deselect-single-selected.directive';

import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { BaseDataRoute, BaseItemsHandler } from '../baseitems-wrapper.component';
import { TABLE_EMPTY } from 'src/app/models/ui/table/table';
import { BaseItem } from '../baseitems-wrapper.component';
import { PreviewThumbnailsComponent } from '../../../_shared/preview-thumbnails/preview-thumbnails.component';
import { ConfirmService } from 'src/app/services/confirm.service';
import { Stage } from 'konva/lib/Stage';
import { Layer } from 'konva/lib/Layer';
import { Line } from 'konva/lib/shapes/Line';
import { Base64Service } from 'src/app/services/base64/base64.service';
import { FormGroupOfType } from 'src/app/types/shared';
import { errorHandle } from 'src/app/misc/functions';

@Component({
  selector: 'app-baseitems-sschf-modal',
  templateUrl: './baseitems-sschf-modal.component.html',
  styleUrl: './baseitems-sschf-modal.component.scss',
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    DeselectSingleSelectedDirective,
    PreviewThumbnailsComponent,
    HideIDColumnsPipe,
    MrTranslatePipe,
    TablePrettyPrintPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class Baseitems_SSCHF_ModalComponent implements BaseItemsHandler {
  private readonly apiService = inject(APIService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly toastr = inject(ToastrService);
  private readonly confirm = inject(ConfirmService);
  private readonly base64 = inject(Base64Service);

  readonly refresh = output<BaseItem[]>();
  readonly isModalOpen = model<boolean>();

  readonly route = model.required<BaseDataRoute>();
  readonly preSelected = computed(() => this.route().selected);

  protected readonly isRep = this.apiService.isRep;

  readonly clrForms = viewChildren(ClrForm);
  readonly skizze = viewChild.required<PreviewThumbnailsComponent>('skizze');
  readonly profilDiv = viewChild.required<ElementRef<HTMLDivElement>>('profil');
  private readonly profilLine = computed(() => {
    const stage = new Stage({
      container: this.profilDiv().nativeElement,
      width: 200,
      height: 200,
    });
    const layer = new Layer({
      x: 100,
      y: 100,
    });
    const line = new Line({
      scaleY: -1,
      stroke: 'darkblue',
    });
    layer.add(line);
    stage.add(layer);
    return line;
  });
  private readonly profil = linkedSignal<string | null>(() => this.selectedRow()?.Profil);
  private readonly points = computed<number[]>(() => unpackProfil(this.profil()));
  private readonly profilChanged = computed(() => this.profil() != this.selectedRow()?.Profil);
  protected readonly hasProfil = computed(() => this.points().length);

  protected readonly selectedRow = signal<SSCHF_Item | null>(null);

  private readonly tableResource = resource({
    params: () => this.isModalOpen() || undefined,
    loader: () => this.apiService.getBaseItems('SSCHF'),
    defaultValue: TABLE_EMPTY(),
  });
  protected readonly table = this.tableResource.value;
  protected readonly isLoading = this.tableResource.isLoading;
  protected readonly loadState = signal(ClrLoadingState.DEFAULT);
  private isChanged = false;

  protected readonly TYPEN: Record<string, SSCHF_Typ> = {
    Vignol: SSCHF_Typ.Vignol,
    Rille: SSCHF_Typ.Rille,
  };

  protected readonly form = new FormGroupOfType<Omit<SSCHF_Item, 'ID'>>({
    Bezeichnung: new FormControl('', Validators.required),
    Typ: new FormControl(SSCHF_Typ.Vignol, {
      nonNullable: true,
      validators: Validators.required,
    }),
    Höhe: new FormControl<number>(null, [Validators.required, Validators.min(1)]),
    Messebene: new FormControl<number>(10, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(1)],
    }),
  });

  constructor() {
    effect(() => this.form.reset(this.selectedRow() ?? undefined));

    effect(() => {
      const preSelect = this.preSelected();
      if (preSelect && this.table().columns.length)
        untracked(() => {
          this.route.update(route => ({ ...route, selected: null }));
          switch (typeof preSelect) {
            case 'string': {
              const found = this.table().rows.find(row => row.Bezeichnung == preSelect);
              if (found) this.selectedRow.set(found);
              else this.form.controls.Bezeichnung.setValue(preSelect);
              break;
            }
            case 'number': {
              this.selectedRow.set({ ID: preSelect } as SSCHF_Item);
              break;
            }
          }
        });
    });

    effect(() => {
      const line = this.profilLine();
      const points = this.points();
      if (!line || !points) return;
      line.points(points);
    });

    effect(() => {
      if (!this.skizze()) return;
      const skizze = this.selectedRow()?.Skizze;
      untracked(() => this.skizze().unpackFiles(skizze && [{ base64: skizze }]));
    });
    effect(() => {
      const status = this.tableResource.status();
      untracked(() => {
        if (status == 'reloading') {
          this.isChanged = true;
        } else if (status == 'resolved' && this.isChanged) {
          this.isChanged = false;
          this.refresh.emit(this.table().rows);
        }
      });
    });
  }

  protected async onSubmit() {
    if (this.form.invalid) {
      this.clrForms().forEach(form => form.markAsTouched());
      return;
    }
    this.loadState.set(ClrLoadingState.LOADING);

    try {
      const sendObj: SSCHF_Item = {
        ID: this.selectedRow()?.ID,
        ...this.form.getRawValue(),
        Skizze: (await this.skizze().packUnsavedFiles())[0]?.base64,
        Profil: this.profil(),
      };

      const id = await this.apiService.setBaseItems('SSCHF', sendObj);

      this.loadState.set(ClrLoadingState.SUCCESS);
      this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert'));
      this.tableResource.reload();
      this.selectedRow.set({ ID: id } as SSCHF_Item);
    } catch (er) {
      this.loadState.set(ClrLoadingState.DEFAULT);
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected async deleteRow() {
    try {
      await this.apiService.deleteBaseItems('SSCHF', this.selectedRow().ID);

      this.toastr.success(this.mrTranslate.transform('Eintrag wurde gelöscht'));
      this.selectedRow.set(null);
      this.tableResource.reload();
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected deleteProfil() {
    this.profil.set(null);
  }

  protected async loadProfil(event: Event) {
    if (!('files' in event.target)) return;
    const file = (event.target as HTMLInputElement).files[0];
    if (!file) return;
    const profil = await this.base64.getTextStringFromFile(file);
    this.profil.set(profil);
  }

  protected async close() {
    const ok = () =>
      this.confirm.warning(`
      ${this.mrTranslate.transform('Sie haben ungespeicherte Änderungen')}.
      ${this.mrTranslate.transform('Trotzdedm schliessen')}?
    `);
    const changed = this.form.dirty || this.skizze().isChanged() || this.profilChanged();
    if (!changed || (await ok())) this.isModalOpen.set(false);
  }
}

export const enum SSCHF_Typ {
  Vignol,
  Rille,
}

export interface SSCHF_Item extends BaseItem {
  Typ: SSCHF_Typ;
  Höhe: number;
  Messebene: number;
  Skizze?: string;
  Profil?: string;
}

function unpackProfil(profil: string): number[] {
  if (profil && /^[-\.\d]+;/.test(profil)) {
    const points = profil
      .split(/\s+/)
      .flatMap(point => point.split(';'))
      .map(point => +point);
    return points;
  }
  return [];
}

/* function packProfil(points: number[]): string {
  const profil: string[] = [];
  for (let i = 0; i < points.length; i+=2) {
    const x = points[i];
    const y = points[i + 1];
    profil.push([x, y].join(';'));
  }

  return profil.join(' ');
} */
